// Library
import { initializeApp } from 'firebase/app';
import { getFirestore } from "firebase/firestore";
import { firebaseConfig } from './FirebaseConfig';
import { runTransaction, doc } from "firebase/firestore";

// Functions
import QueryDocument from '../Library/QueryDocument';

/**
 * Creates a new request and returns request ID(s)
 * @param {number} idCount
 * 
 * ```
 * const idCount = 2;
 * 
 *
 * CreateRequest(idCount).then((requestids) =>{
 * 
 *   const digitalRequestId = requestids[0];
 *   const initialApprovalRequestId = requestids[1];
 * 
 * });
 * 
 * ```
 * 
 * Documentation
 * - N/A
 */

export default async function CreateRequest(idCount, retryCount, requestIds) {

  try {

    //Firestore Database reference
    const app = initializeApp(firebaseConfig);
    const db = getFirestore(app);

    //------------------------------------------------------
    //  Validate number of IDs requested
    //------------------------------------------------------

    if (idCount === undefined) {

      idCount = 1;

    }

    //------------------------------------------------------
    //  Validate number of retry attempts
    //------------------------------------------------------

    if (retryCount === undefined) {

      retryCount = 0;

    }

    if (retryCount > 3) throw new Error(`Function CreateRequest failed exceed maxiumum attempts of 3.`);

    //------------------------------------------------------
    //  Validate requestIds
    //------------------------------------------------------

    if (requestIds === undefined) {

      requestIds = [];

    }

    //------------------------------------------------------
    //  Check if all request IDs have been created
    //------------------------------------------------------

    if (idCount === requestIds.length) {

      return requestIds;

    }

    //------------------------------------------------------
    //  Create a Firebase transaction
    //    1. Query for the previous document
    //    2. Create the next document
    //    IF there is a conflict, retry up to three attempts!
    //------------------------------------------------------

    const requests = await QueryDocument('requests', [
      ['tasktype', 'not-in', ['Commercial Review', 'Digital Commercial Request']]
    ], ['requestid', 'desc'], 1);

    return runTransaction(db, async (transaction) => {

      //------------------------------------------------------
      //  Define NEW task number based off the previous 'requests'
      //------------------------------------------------------

      const previousRequestId = requests[0]?.requestid;
      let requestid = 'req-';

      // Create or Increment task number
      if (requests.length === 0 || previousRequestId === undefined) {

        requestid = `${requestid}0000001`; // No existing requests found, start from 0

      } else {

        //------------------------------------------------------
        //  Increment from existing request
        //------------------------------------------------------

        const previousNumber = parseInt(previousRequestId.slice(4)); // Extract number from requestid 
        const newNumber = previousNumber + 1;

        // Add required zero's
        const loopLimit = 7 - `${newNumber}`.length;

        let count = 0;
        while (count < loopLimit) {

          count += 1;

          requestid = `${requestid}0`;

        }

        // Finally, add 1 to the task number
        requestid = `${requestid}${newNumber}`;

      }

      //------------------------------------------------------
      //  Using a 'transaction' check if document exists!
      //------------------------------------------------------

      const docRef = doc(db, 'requests', requestid);
      const docCheck = await transaction.get(docRef);

      if (docCheck.exists()) throw new Error('Found existing document!');

      //------------------------------------------------------
      //  Generate the request ID
      //------------------------------------------------------

      const document = {
        'requestid': requestid,
        'createddate': new Date(),
        'tasktype': '',
        'createdby': {
          'email': '',
          'givenname': '',
          'surname': ''
        },
        'lastmodifiedby': {
          'email': '',
          'givenname': '',
          'surname': ''
        }
      };

      //Set collection
      const collectionDocRef = doc(db, 'requests', requestid);

      //------------------------------------------------------
      //  Using a 'transaction' write the new document
      //------------------------------------------------------

      transaction.set(collectionDocRef, document, { merge: false });

      return requestid;

    })

    //------------------------------------------------------
    //  Settle 'runTransaction'
    //------------------------------------------------------

    .then((requestid) => {

      requestIds.push(requestid);

      return CreateRequest(idCount, retryCount, requestIds);

    })
    
    //------------------------------------------------------
    //  'runTransaction' failed, re-run with incremented 'retryCount' 
    //------------------------------------------------------

    .catch((error) => {

      console.log('Failed to create request', error);

      retryCount += 1;

      // Yes this is a loop, but it has a backoff :-)
      return CreateRequest(idCount, retryCount, requestIds);

    });

    //------------------------------------------------------
    //  Handle unexpected errors
    //------------------------------------------------------

  } catch (error) {

    throw new Error(`Function CreateRequest failed to complete, error ${error}`);

  }

}
